import { createSlice } from '@reduxjs/toolkit';
import { LOADING_STATUS } from '../../../shared/constants/loadingStatus';

const initialState = {
  campaignsStatus: LOADING_STATUS.IDLE,
  tagsStatus: LOADING_STATUS.IDLE,
  triggersStatus: LOADING_STATUS.IDLE,
  variablesStatus: LOADING_STATUS.IDLE,
  tenantSettingsStatus: LOADING_STATUS.IDLE,
  disclosuresStatus: LOADING_STATUS.IDLE,
  tenantsStatus: LOADING_STATUS.IDLE,
  campaigns: [],
  tags: [],
  triggers: [],
  variables: [],
  variableSearch: {
    results: [],
    requestId: null,
    hasLoaded: false,
  },
  disclosures: [],
  tenants: [],
  tenantLogo: null,
  tenantSender: null,
  tenantVariablesSyncedAt: null,
  tenantAdditionalLanguages: [],
};

export const templatesMetaSlice = createSlice({
  name: 'templatesMeta',
  initialState,
  reducers: {
    templatesCampaignsRequested(state) {
      state.campaignsStatus = LOADING_STATUS.LOADING;
    },
    templatesCampaignsReceived(state, action) {
      state.campaignsStatus = LOADING_STATUS.SUCCEEDED;
      state.campaigns = action.payload ?? [];
    },
    templatesCampaignsFailed(state) {
      state.campaignsStatus = LOADING_STATUS.FAILED;
    },
    templatesTagsRequested(state) {
      state.tagsStatus = LOADING_STATUS.LOADING;
    },
    templatesTagsReceived(state, action) {
      state.tagsStatus = LOADING_STATUS.SUCCEEDED;
      state.tags = action.payload ?? [];
    },
    templatesTagsFailed(state) {
      state.tagsStatus = LOADING_STATUS.FAILED;
    },
    templatesTriggersRequested(state) {
      state.triggersStatus = LOADING_STATUS.LOADING;
    },
    templatesTriggersReceived(state, action) {
      state.triggersStatus = LOADING_STATUS.SUCCEEDED;
      state.triggers = action.payload ?? [];
    },
    templatesTriggersFailed(state) {
      state.triggersStatus = LOADING_STATUS.FAILED;
    },
    templatesTriggersReset(state) {
      state.triggersStatus = LOADING_STATUS.IDLE;
      state.triggers = [];
    },
    templatesVariablesRequested(state) {
      state.variablesStatus = LOADING_STATUS.LOADING;
    },
    templatesVariablesReceived(state, action) {
      state.variablesStatus = LOADING_STATUS.SUCCEEDED;
      state.variables = action.payload ?? [];
    },
    templatesVariablesFailed(state) {
      state.variablesStatus = LOADING_STATUS.FAILED;
    },
    templatesVariablesReset(state) {
      state.variablesStatus = LOADING_STATUS.IDLE;
      state.variables = [];
      state.variableSearch = initialState.variableSearch;
    },
    templatesVariableSearchRequested(state, action) {
      state.variableSearch.requestId = action.payload?.requestId ?? null;
    },
    templatesVariableSearchReceived(state, action) {
      if (state.variableSearch.requestId !== action.payload?.requestId) return;
      state.variableSearch.results = action.payload?.results ?? [];
      state.variableSearch.hasLoaded = true;
    },
    templatesVariableSearchReset(state) {
      state.variableSearch = initialState.variableSearch;
    },
    templatesTenantSettingsRequested(state) {
      state.tenantSettingsStatus = LOADING_STATUS.LOADING;
    },
    templatesTenantSettingsReceived(state, action) {
      state.tenantSettingsStatus = LOADING_STATUS.SUCCEEDED;
      state.tenantLogo = action.payload?.logo ?? null;
      state.tenantSender = {
        name: action.payload?.sender_name ?? null,
        address: action.payload?.sender_address ?? null,
        city: action.payload?.sender_city ?? null,
        state: action.payload?.sender_state ?? null,
        zip: action.payload?.sender_zip ?? null,
        unsubscribePage: action.payload?.unsubscribe_page ?? null,
      };
      state.tenantVariablesSyncedAt = action.payload?.variables_synced_at ?? null;
      state.tenantAdditionalLanguages = action.payload?.additional_language ?? [];
    },
    templatesTenantSettingsFailed(state) {
      state.tenantSettingsStatus = LOADING_STATUS.FAILED;
    },
    templatesDisclosuresRequested(state) {
      state.disclosuresStatus = LOADING_STATUS.LOADING;
    },
    templatesDisclosuresReceived(state, action) {
      state.disclosuresStatus = LOADING_STATUS.SUCCEEDED;
      state.disclosures = action.payload ?? [];
    },
    templatesDisclosuresFailed(state) {
      state.disclosuresStatus = LOADING_STATUS.FAILED;
    },
    templatesTenantsRequested(state) {
      state.tenantsStatus = LOADING_STATUS.LOADING;
    },
    templatesTenantsReceived(state, action) {
      state.tenantsStatus = LOADING_STATUS.SUCCEEDED;
      state.tenants = (action.payload ?? []).map((tenant) => ({
        id: tenant.tenant_id,
        name: tenant.tenant_name || tenant.domain || tenant.tenant_id,
      }));
    },
    templatesTenantsFailed(state) {
      state.tenantsStatus = LOADING_STATUS.FAILED;
    },
  },
});

export const templatesMetaReducer = templatesMetaSlice.reducer;
export const {
  templatesCampaignsRequested,
  templatesCampaignsReceived,
  templatesCampaignsFailed,
  templatesTagsRequested,
  templatesTagsReceived,
  templatesTagsFailed,
  templatesTriggersRequested,
  templatesTriggersReceived,
  templatesTriggersFailed,
  templatesTriggersReset,
  templatesVariablesRequested,
  templatesVariablesReceived,
  templatesVariablesFailed,
  templatesVariablesReset,
  templatesVariableSearchRequested,
  templatesVariableSearchReceived,
  templatesVariableSearchReset,
  templatesTenantSettingsRequested,
  templatesTenantSettingsReceived,
  templatesTenantSettingsFailed,
  templatesDisclosuresRequested,
  templatesDisclosuresReceived,
  templatesDisclosuresFailed,
  templatesTenantsRequested,
  templatesTenantsReceived,
  templatesTenantsFailed,
} = templatesMetaSlice.actions;

export const selectTemplatesCampaignsStatus = (state) => state.templates.meta.campaignsStatus;
export const selectTemplatesCampaigns = (state) => state.templates.meta.campaigns;

export const selectTemplatesTagsStatus = (state) => state.templates.meta.tagsStatus;
export const selectTemplatesTags = (state) => state.templates.meta.tags;

export const selectTemplatesTriggersStatus = (state) => state.templates.meta.triggersStatus;
export const selectTemplatesTriggers = (state) => state.templates.meta.triggers;

export const selectTemplatesVariablesStatus = (state) => state.templates.meta.variablesStatus;
export const selectTemplatesVariables = (state) => state.templates.meta.variables;
export const selectTemplatesVariableSearch = (state) => state.templates.meta.variableSearch;

export const selectTemplatesTenantSettingsStatus = (state) =>
  state.templates.meta.tenantSettingsStatus;
export const selectTemplatesTenantLogo = (state) => state.templates.meta.tenantLogo;
export const selectTemplatesTenantSender = (state) => state.templates.meta.tenantSender;
export const selectTemplatesTenantVariablesSyncedAt = (state) =>
  state.templates.meta.tenantVariablesSyncedAt;
export const selectTemplatesTenantAdditionalLanguages = (state) =>
  state.templates.meta.tenantAdditionalLanguages;

export const selectTemplatesDisclosuresStatus = (state) => state.templates.meta.disclosuresStatus;
export const selectTemplatesDisclosures = (state) => state.templates.meta.disclosures;

export const selectTemplatesTenantsStatus = (state) => state.templates.meta.tenantsStatus;
export const selectTemplatesTenants = (state) => state.templates.meta.tenants;
